import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { GraphDef, buildQuery, searchMetrics } from './api';

const AGGREGATIONS = ['avg', 'sum', 'max', 'min'];

// Line-color choices, picked to read well in both themes.
const SWATCHES = [
  '#8F93FF',
  '#4E4CCD',
  '#34D399',
  '#059669',
  '#F5A623',
  '#D97706',
  '#FF385F',
  '#E11D48',
  '#5EC8E5',
  '#B78CFF',
  '#6B7280',
];

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  metricWrapper: {
    position: 'relative',
    marginBottom: theme.spacing(2),
  },
  metricRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: theme.spacing(1),
  },
  metricName: {
    fontFamily: 'monospace',
    fontSize: 13,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  labelField: {
    width: 180,
    flexShrink: 0,
  },
  swatch: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.divider}`,
    display: 'inline-block',
  },
  swatchMenu: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 24px)',
    gap: 6,
    padding: theme.spacing(1),
  },
  swatchOption: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    padding: 0,
  },
  suggestions: {
    position: 'absolute',
    zIndex: theme.zIndex.modal + 1,
    left: 0,
    right: 0,
    maxHeight: 240,
    overflowY: 'auto',
  },
  suggestionText: {
    fontFamily: 'monospace',
    fontSize: 13,
  },
  searchError: {
    color: theme.palette.error.main,
    fontSize: 12,
    marginTop: theme.spacing(0.5),
  },
  queryPreview: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: theme.palette.text.secondary,
    wordBreak: 'break-all',
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.03)',
  },
}));

/**
 * Add/edit dialog for one graph. Simple mode builds the query from metric
 * (with autocomplete against Datadog's metric search), aggregation, tag
 * filter and group-by; raw mode edits the Datadog query string directly.
 */
export function GraphDialog({
  open,
  initial,
  metricSeed,
  sectionSeed,
  sections = [],
  onClose,
  onSave,
}: {
  open: boolean;
  /** The graph being edited; undefined when adding a new one. */
  initial?: GraphDef;
  /** Pre-filled metric search term for new graphs, e.g. `savr.my_service`. */
  metricSeed?: string;
  /** Pre-selected section for new graphs (the active tab). */
  sectionSeed?: string;
  /** Existing section names, offered as suggestions. */
  sections?: string[];
  onClose: () => void;
  onSave: (graph: GraphDef) => void;
}) {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // An existing graph without stored builder fields (e.g. hand-written in the
  // annotation) can only be edited as a raw query.
  const initialRaw = Boolean(initial && !initial.metrics?.length);
  const [rawMode, setRawMode] = useState(initialRaw);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [rawQuery, setRawQuery] = useState(initial?.query ?? '');
  // A graph can plot several metrics; picked ones become removable chips and
  // the text field keeps working for searching/adding the next one.
  const [metrics, setMetrics] = useState<string[]>(initial?.metrics ?? []);
  // Optional display names per metric, shown in the legend instead of the
  // full metric name.
  const [labels, setLabels] = useState<Record<string, string>>(
    initial?.labels ?? {},
  );
  // Optional per-metric line colors; empty string / missing means automatic.
  const [colors, setColors] = useState<Record<string, string>>(
    initial?.colors ?? {},
  );
  const [colorMenu, setColorMenu] = useState<{
    metric: string;
    anchor: HTMLElement;
  } | null>(null);
  const [section, setSection] = useState(
    initial ? initial.section ?? '' : sectionSeed ?? '',
  );
  const [metricInput, setMetricInput] = useState(
    initial?.metrics?.length ? '' : metricSeed ?? '',
  );
  const [aggregation, setAggregation] = useState(initial?.aggregation ?? 'avg');
  const [filter, setFilter] = useState(initial?.filter ?? '');
  const [groupBy, setGroupBy] = useState(initial?.groupBy ?? '');
  const [xLabel, setXLabel] = useState(initial?.xLabel ?? '');
  const [yLabel, setYLabel] = useState(initial?.yLabel ?? '');
  const [chartType, setChartType] = useState<'line' | 'pie' | 'toplist'>(
    initial?.chartType ?? 'line',
  );
  const [externalVisible, setExternalVisible] = useState(
    initial?.externalVisible ?? false,
  );

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchError, setSearchError] = useState<string>();
  const suppressSearch = useRef(false);

  const addMetric = (name: string) => {
    const trimmed = name.trim();
    if (trimmed && !metrics.includes(trimmed)) {
      setMetrics([...metrics, trimmed]);
    }
    suppressSearch.current = true;
    setMetricInput('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Debounced metric-name autocomplete.
  useEffect(() => {
    if (rawMode || metricInput.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    if (suppressSearch.current) {
      suppressSearch.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      searchMetrics({ discoveryApi, fetchApi }, metricInput.trim())
        .then(result => {
          setSuggestions(result.slice(0, 25));
          setSearchError(undefined);
        })
        .catch(e => {
          setSuggestions([]);
          setSearchError(`Metric search failed: ${e.message ?? e}`);
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [metricInput, rawMode, discoveryApi, fetchApi]);

  // A typed-but-not-yet-added metric counts too, so a single metric never
  // needs an explicit "add" step.
  const effectiveMetrics = useMemo(() => {
    const pending = metricInput.trim();
    return pending && !metrics.includes(pending)
      ? [...metrics, pending]
      : metrics;
  }, [metrics, metricInput]);

  const query = useMemo(() => {
    if (rawMode) {
      return rawQuery.trim();
    }
    if (!effectiveMetrics.length) {
      return '';
    }
    return buildQuery({
      metrics: effectiveMetrics,
      aggregation,
      filter,
      groupBy,
    });
  }, [rawMode, rawQuery, effectiveMetrics, aggregation, filter, groupBy]);

  // Untitled graphs fall back to the metric name(s).
  const finalTitle =
    title.trim() || (rawMode ? '' : effectiveMetrics.join(', '));
  const valid = Boolean(query) && Boolean(finalTitle);

  const handleSave = () => {
    const perMetric = (map: Record<string, string>) =>
      Object.fromEntries(
        Object.entries(map).filter(
          ([metric, value]) =>
            effectiveMetrics.includes(metric) && value.trim(),
        ),
      );
    const savedLabels = perMetric(labels);
    const savedColors = perMetric(colors);
    const graph: GraphDef = {
      title: finalTitle,
      query,
      ...(rawMode
        ? {}
        : {
            metrics: effectiveMetrics,
            aggregation,
            filter: filter.trim() || undefined,
            groupBy: groupBy.trim() || undefined,
          }),
      ...(Object.keys(savedLabels).length ? { labels: savedLabels } : {}),
      ...(Object.keys(savedColors).length ? { colors: savedColors } : {}),
      xLabel: xLabel.trim() || undefined,
      yLabel: yLabel.trim() || undefined,
      section: section.trim() || undefined,
      chartType: chartType === 'line' ? undefined : chartType,
      externalVisible: externalVisible || undefined,
    };
    onSave(graph);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? 'Edit graph' : 'Add graph'}</DialogTitle>
      <DialogContent>
        <FormControlLabel
          className={classes.field}
          control={
            <Switch
              checked={rawMode}
              onChange={event => setRawMode(event.target.checked)}
              size="small"
              color="primary"
            />
          }
          label="Raw Datadog query"
        />
        {rawMode ? (
          <TextField
            className={classes.field}
            label="Query"
            placeholder="avg:savr.my_service.some_metric{env:production} by {host}"
            value={rawQuery}
            onChange={event => setRawQuery(event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        ) : (
          <>
            {metrics.map(name => (
              <div key={name} className={classes.metricRow}>
                <span className={classes.metricName} title={name}>
                  {name}
                </span>
                <TextField
                  className={classes.labelField}
                  placeholder="Label (optional)"
                  value={labels[name] ?? ''}
                  onChange={event =>
                    setLabels({ ...labels, [name]: event.target.value })
                  }
                  variant="outlined"
                  size="small"
                />
                <Tooltip title="Line color">
                  <IconButton
                    size="small"
                    onClick={event =>
                      setColorMenu({
                        metric: name,
                        anchor: event.currentTarget,
                      })
                    }
                  >
                    <span
                      className={classes.swatch}
                      style={
                        colors[name]
                          ? { backgroundColor: colors[name] }
                          : {
                              background:
                                'conic-gradient(#8F93FF, #34D399, #F5A623, #FF385F, #8F93FF)',
                            }
                      }
                    />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={() => setMetrics(metrics.filter(m => m !== name))}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Menu
              open={Boolean(colorMenu)}
              anchorEl={colorMenu?.anchor}
              onClose={() => setColorMenu(null)}
            >
              <div className={classes.swatchMenu}>
                {SWATCHES.map(hex => (
                  <button
                    key={hex}
                    type="button"
                    aria-label={`Use color ${hex}`}
                    className={classes.swatchOption}
                    style={{ backgroundColor: hex }}
                    onClick={() => {
                      if (colorMenu) {
                        setColors({ ...colors, [colorMenu.metric]: hex });
                      }
                      setColorMenu(null);
                    }}
                  />
                ))}
              </div>
              <MenuItem
                dense
                onClick={() => {
                  if (colorMenu) {
                    const { [colorMenu.metric]: _removed, ...rest } = colors;
                    setColors(rest);
                  }
                  setColorMenu(null);
                }}
              >
                Automatic
              </MenuItem>
            </Menu>
            <div className={classes.metricWrapper}>
              <TextField
                label={metrics.length ? 'Add another metric' : 'Metric'}
                placeholder="Start typing to search metric names"
                value={metricInput}
                onChange={event => {
                  setMetricInput(event.target.value);
                  setShowSuggestions(true);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' && metricInput.trim()) {
                    event.preventDefault();
                    addMetric(metricInput);
                  }
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() =>
                  // Delayed so a click on a suggestion still lands.
                  setTimeout(() => setShowSuggestions(false), 200)
                }
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  style: { fontFamily: 'monospace', fontSize: 13 },
                }}
              />
              {showSuggestions && suggestions.length > 0 && (
                <Paper className={classes.suggestions} elevation={4}>
                  <List dense disablePadding>
                    {suggestions.map(name => (
                      <ListItem
                        key={name}
                        button
                        onClick={() => addMetric(name)}
                      >
                        <ListItemText
                          primary={name}
                          classes={{ primary: classes.suggestionText }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
              {searchError && (
                <Typography className={classes.searchError}>
                  {searchError}
                </Typography>
              )}
            </div>
            <FormControl
              className={classes.field}
              variant="outlined"
              size="small"
              fullWidth
            >
              <InputLabel id="datadog-aggregation-label">
                Aggregation
              </InputLabel>
              <Select
                labelId="datadog-aggregation-label"
                label="Aggregation"
                value={aggregation}
                onChange={event => setAggregation(event.target.value as string)}
              >
                {AGGREGATIONS.map(agg => (
                  <MenuItem key={agg} value={agg}>
                    {agg}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              className={classes.field}
              label="Filter tags (optional)"
              placeholder="env:production, service:my-service - defaults to *"
              value={filter}
              onChange={event => setFilter(event.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
            <TextField
              className={classes.field}
              label="Group by (optional)"
              placeholder="host"
              value={groupBy}
              onChange={event => setGroupBy(event.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          </>
        )}
        <FormControl
          className={classes.field}
          variant="outlined"
          size="small"
          fullWidth
        >
          <InputLabel id="graph-chart-type-label">Chart type</InputLabel>
          <Select
            labelId="graph-chart-type-label"
            label="Chart type"
            value={chartType}
            onChange={event =>
              setChartType(event.target.value as 'line' | 'pie' | 'toplist')
            }
          >
            <MenuItem value="line">Timeseries (lines)</MenuItem>
            <MenuItem value="pie">Pie - share per series</MenuItem>
            <MenuItem value="toplist">Top list - ranked totals</MenuItem>
          </Select>
        </FormControl>
        <TextField
          className={classes.field}
          label="Y-axis label (optional)"
          placeholder="e.g. orders/s"
          value={yLabel}
          onChange={event => setYLabel(event.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />
        <TextField
          className={classes.field}
          label="X-axis label (optional)"
          placeholder="Usually empty - the x-axis is time"
          value={xLabel}
          onChange={event => setXLabel(event.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />
        <FormControlLabel
          className={classes.field}
          control={
            <Switch
              checked={externalVisible}
              onChange={event => setExternalVisible(event.target.checked)}
              size="small"
              color="primary"
            />
          }
          label="Visible to external users"
        />
        <TextField
          className={classes.field}
          label="Section (optional)"
          placeholder="e.g. Errors, Performance - sections render as tabs"
          value={section}
          onChange={event => setSection(event.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          inputProps={{ list: 'datadog-graph-sections' }}
        />
        <datalist id="datadog-graph-sections">
          {sections.map(name => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <TextField
          className={classes.field}
          label="Title"
          placeholder={effectiveMetrics.join(', ') || 'Graph title'}
          value={title}
          onChange={event => setTitle(event.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />
        {query && (
          <Typography component="div" className={classes.queryPreview}>
            {query}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disableElevation
          disabled={!valid}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
